import { useEffect, useMemo, useState } from "react";
import { useStore, type ArtifactSummary } from "@/lib/store";
import { ArtifactDetail } from "./ArtifactDetail";

const ARTIFACT_TOOL_NAMES = new Set([
  "artifact_create",
  "artifact_write",
  "artifact_edit",
  "artifact_read",
  "artifact_delete",
]);

function extractArtifactIds(messages: Array<{ toolCalls?: Array<{ tool: string; args?: Record<string, unknown>; result?: unknown }> }>): string[] {
  const ids = new Set<string>();
  for (const m of messages) {
    for (const tc of m.toolCalls ?? []) {
      if (!ARTIFACT_TOOL_NAMES.has(tc.tool)) continue;
      const fromArgs = tc.args?.id;
      if (typeof fromArgs === "string") ids.add(fromArgs);
      if (tc.result && typeof tc.result === "object") {
        const fromResult = (tc.result as Record<string, unknown>).id;
        if (typeof fromResult === "string") ids.add(fromResult);
      }
    }
  }
  return Array.from(ids);
}

export function ArtifactStrip({ onOpenLibrary }: { onOpenLibrary: () => void }) {
  const open = useStore((s) => s.artifactPanelOpen);
  const externalSelectedId = useStore((s) => s.artifactPanelSelectedId);
  const closeArtifactPanel = useStore((s) => s.closeArtifactPanel);
  const messages = useStore((s) => s.messages);
  const artifacts = useStore((s) => s.artifacts);
  const refreshArtifacts = useStore((s) => s.refreshArtifacts);
  const [selectedId, setSelectedId] = useState<string | null>(externalSelectedId);

  useEffect(() => {
    if (open) refreshArtifacts();
  }, [open, refreshArtifacts]);

  useEffect(() => {
    if (open) setSelectedId(externalSelectedId);
  }, [open, externalSelectedId]);

  const referenced = useMemo<ArtifactSummary[]>(() => {
    const ids = new Set(extractArtifactIds(messages as any));
    return artifacts.filter((a) => ids.has(a.id));
  }, [artifacts, messages]);

  if (!open) return null;

  const body = (
    <aside className="flex h-full w-full max-w-full flex-col bg-background lg:border-l shadow-xl lg:shadow-none">
      <div data-tauri-drag-region className="flex items-center gap-2 border-b px-4 py-2.5" style={{ paddingTop: "calc(0.625rem + var(--titlebar-h, 0px) + env(safe-area-inset-top))" }}>
        <i className="fa-solid fa-file-code text-primary" />
        <h2 className="text-sm font-semibold">In this chat</h2>
        <span className="text-xs text-muted-foreground">{referenced.length}</span>
        <button
          onClick={onOpenLibrary}
          className="ml-auto rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
          title="Open library"
        >
          All artifacts <i className="fa-solid fa-arrow-right text-[10px]" />
        </button>
        <button onClick={closeArtifactPanel} className="rounded-md p-1.5 text-muted-foreground hover:text-foreground" title="Close">
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      {selectedId ? (
        <ArtifactDetail id={selectedId} onClose={() => setSelectedId(null)} />
      ) : referenced.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">
          No artifacts referenced in this chat yet.
          <div className="mt-3">
            <button onClick={onOpenLibrary} className="text-primary hover:underline">
              Browse all artifacts
            </button>
          </div>
        </div>
      ) : (
        <ul className="overflow-y-auto">
          {referenced.map((a) => (
            <li key={a.id}>
              <button
                onClick={() => setSelectedId(a.id)}
                className="flex w-full flex-col items-start gap-0.5 border-b px-4 py-2.5 text-left hover:bg-muted/50"
              >
                <span className="truncate text-sm font-medium">{a.name}</span>
                <span className="text-[11px] text-muted-foreground">{a.fileCount} files</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );

  return (
    <>
      <div className="fixed inset-0 z-30 flex lg:hidden">
        <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={closeArtifactPanel} />
        <div className="w-full max-w-md h-full">{body}</div>
      </div>
      <div className="hidden lg:block fixed right-0 top-0 bottom-0 w-[32rem] z-20">{body}</div>
    </>
  );
}
